import * as inflection from 'inflection'

import apps from './locales/vmisc/apps.json'
import auth from './locales/vmisc/auth.json'
import settings from './locales/vmisc/settings.json'
import executions from './locales/vmisc/executions.json'
import onboarding from './locales/vmisc/onboarding.json'
import organizations from './locales/vmisc/organizations.json'
import flowNotifications from './locales/vmisc/flow_notifications.json'

import helpers from './locales/util/helpers.json'

import start from './locales/start.json'
import studio from './locales/studio.json'
import general from './locales/general.json'
import collections from './locales/collections.json'
import presentation from './locales/presentation.json'
import schema from './locales/schema.json'

import basicsForm from './locales/apps/basics_forms.json'
import data from './locales/apps/data.json'
import documents from './locales/apps/documents.json'
import process from './locales/apps/process.json'
import textAi from './locales/apps/text_ai.json'

const localesMapping = {
  apps,
  auth,
  settings,
  executions,
  onboarding,
  organizations,
  flowNotifications,
  helpers,
  start,
  studio,
  general,
  collections,
  presentation,
  schema,
  basicsForm,
  data,
  documents,
  process,
  textAi
}

const ID_PATTERN = /^[A-Za-z0-9]{5}$/

function getUserLanguage() {
  let stored = 'auto';
  try {
    if (typeof localStorage !== 'undefined') {
      const v = localStorage.getItem('language');
      if (v) stored = v;
    }
  } catch (_) {
    console.warn("No se pudo acceder a localStorage");
  }
  
  if (stored === 'es' || stored === 'en') return stored;

  const code = (
    typeof navigator !== 'undefined'
      ? navigator.language
      : process?.env?.LANG
  )?.split(/[-_.]/)[0];

  return code === 'en' ? 'en' : 'es';
}

function applyPlaceholders(text, params) {
  return (text ?? "").replace(/\{ *(\w+) *\}/g, (_, key) =>
    key in params ? params[key] : `{${key}}`
  )
}

function formatText(value, fmt) {
  if (!fmt) return value

  if (fmt.toLowerCase() === 'aa') return value.toLowerCase()
  if (fmt === 'AA') return value.toUpperCase()

  let hasArray = false
  if (value.endsWith('[]')) {
    hasArray = true
    value = value.slice(0, -2)
  }

  let suffixChar = ''
  if (/^[/.]/.test(fmt)) {
    suffixChar = fmt[0]
    fmt = fmt.slice(1)
    if (value) value = suffixChar + value
  }

  if (fmt.includes('_')) {
    value = inflection.underscore(value)
  } else if (fmt.includes(' ')) {
    value = inflection.underscore(value).replace(/_/g, ' ')
  } else {
    value = inflection.camelize(value, fmt.startsWith('A'))
    if (!fmt.startsWith('A')) {
      value = value[0].toLowerCase() + value.slice(1)
    }
  }

  if (/[/.]$/.test(fmt)) {
    suffixChar = fmt.slice(-1)
    fmt = fmt.slice(0, -1)
    if (value) value += suffixChar
  }

  if (/s$/.test(fmt)) {
    const lang = getUserLanguage()
    value =
      lang === 'es'
        ? value.endsWith('s')
          ? value
          : value + 's'
        : inflection.pluralize(value)
  } else if (/u$/.test(fmt)) {
    value = inflection.singularize(value)
  }

  if (fmt.startsWith('A_A') || fmt.startsWith('A A')) {
    value = value.toUpperCase()
  } else if (fmt.startsWith('A')) {
    value = value[0].toUpperCase() + value.slice(1)
  } else if (fmt.startsWith('a')) {
    value = value[0].toLowerCase() + value.slice(1)
  }

  return hasArray ? value + '[]' : value
}

const norm = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFC')
    .trim()
    .toLowerCase()

const cleanId = (s) =>
  (s ?? '')
    .toString()
    .trim()
    .toUpperCase()

function findById(id) {
  for (const file of Object.values(localesMapping)) {
    for (const key in file) {
      const [origRaw, keyIdRaw] = key.split('%')
      if (cleanId(keyIdRaw) === id) {
        return { value: file[key], orig: (origRaw ?? '').toString() }
      }
    }
  }
  return null
}

/**
 * Translates a string with key "DefaultText%ABCDE",
 * validates that the ID has 5 alphanumeric characters,
 * handles placeholders and formatting.
 *
 * @param {string} content — "Default text%ABCDE"
 * @param {object} params  — { placeholder: 'value', ... }
 * @param {string|null} format — optional format ('aA', 'As', etc.)
 */
export default function l(content, params = {}, format = null) {
  const isDeveloper = sessionStorage.getItem('is_developer') == 'true'
  const languageStored = localStorage.getItem("language"); 
  const lang = getUserLanguage()

  const findById = (id) => {
    for (const file of Object.values(localesMapping)) {
      for (const key in file) {
        const parts = key.split('%')
        if (parts.length < 2) continue
        const keyId = cleanId(parts[1])
        if (keyId === id) return file[key]
      }
    }
    return null
  }

  if (typeof content !== 'string' || !content.includes('%')) {
    const txt = applyPlaceholders((content ?? '').toString(), params)
    return formatText(txt, format)
  }

  const [defaultTextRaw, idRaw] = content.split('%', 2)
  const defaultText = (defaultTextRaw ?? '').toString()
  const id = cleanId(idRaw)

  if (!ID_PATTERN.test(id)) {
    if (isDeveloper) {
      throw new Error(
        `Clave inválida “${idRaw}”: el %ID debe ser 5 caracteres alfanuméricos.`
      )
    }
    console.warn(
      `[l] ID inválido en clave "${content}". Mostrando texto sin %ID.`
    )
    const txt = applyPlaceholders(defaultText, params)
    return formatText(txt, format)
  }

  let translationFound = null
  const defaultNorm = norm(defaultText)
  outer: for (const file of Object.values(localesMapping)) {
    for (const key in file) {
      const [origRaw, keyIdRaw] = key.split('%')
      if (cleanId(keyIdRaw) !== id) continue
      if (norm(origRaw) === defaultNorm) {
        translationFound = { value: file[key], orig: (origRaw ?? '').toString() }
        break outer
      }
    }
  }

  if (!translationFound) {
    if (isDeveloper) {
      throw new Error(`No se encontró texto para la clave “${content}”.`)
    }

    const byId = findById(id)
    if (byId) {
      let txt: string
      if (lang === 'es') {
        txt = byId.orig || defaultText
      } else {
        txt = (byId.value?.en ?? byId.orig ?? defaultText).toString()
      }
      txt = applyPlaceholders(txt, params)
      console.warn(
        `[l] Clave sin match exacto "${content}". Usando fallback por ID %${id}.`
      )
      return formatText(txt, format)
    }

    console.warn(
      `[l] Clave no encontrada "${content}". Mostrando texto sin %ID.`
    )
    const txt = applyPlaceholders(defaultText, params)
    return formatText(txt, format)
  }

  if (isDeveloper) {
    return content
  }

  if (lang === 'es') {
    const txt = applyPlaceholders(defaultText, params)
    return formatText(txt, format)
  }

  let txt = (translationFound.value?.en ?? defaultText).toString()
  txt = applyPlaceholders(txt, params)
  return formatText(txt, format)
}
